import React, { useState, useEffect } from 'react';
import { FaChevronDown, FaMapPin } from "react-icons/fa";
import { Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../constants/config';
import axiosPost from '../helpers/axios-helper';

import '../styles/Country.css';

export default function Country() {
    const location = useLocation();
    const { slug } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [countryInfo, setCountryInfo] = useState(null);
    const [countryArticles, setCountryArticles] = useState([]);
    
    // Resolved country ID (from id query or slug)
    const [countryId, setCountryId] = useState(null);
    
    const [ country, setCountry ] = useState({
        name: 'Curacao',
        image: 'https://e22hgs9rtwc.exactdn.com/wp-content/uploads/2021/11/shutterstock_2262892975.jpg?strip=all&lossy=1&ssl=1',
        intro: {
            title: 'Curacao Travel Guide',
            body: "Discover the vibrant beauty of Curaçao, an island where the shimmering turquoise waters of the Caribbean Sea gently kiss the shores of pristine, white-sand beaches. Picture yourself wandering through the charming streets of Willemstad, the island's capital, where rows of colorful colonial buildings, painted in shades of pastel pinks, yellows, and blues, create a stunning contrast against the bright blue sky. Every corner of this UNESCO World Heritage city tells a story, from its rich history to its vibrant cultural scene."
        },
        blogs: [
            {
                id: 1,
                name: 'BLUE BAY BEACH',
                desc: 'Blue Bay Beach in Curaçao is a stunning tropical haven known for its crystal-clear waters, soft white sand, and vibrant coral reefs.',
                author: 'JANICE TJON',
                image: 'https://images.ctfassets.net/m868ks80jg7q/60Ge4jrqONABiaI3PffWRm/7a572b8e0459a9161410ecdcbcd39c3e/cc-blue-bay-beach.jpg?w=600&h=415&fm=webp&fit=fill&q=75'
            },
            {
                id: 2,
                name: 'CASCADA ROOFTOP BAR + KITCHEN',
                desc: "Cascada Rooftop Bar + Kitchen is Curaçao's most stunning rooftop experience, bar, and lounge! Enjoy 360° views…",
                author: 'ELIZABETH HEATH',
                image: 'https://www.cascadacuracao.com/lib/eh2m1o/Elements---Drone-1-lrgvedqx.jpg'
            },
        ]

    })

    const toAbsUrl = (url, fallback = '') => {
        if (!url) return fallback;
        if (/^https?:\/\//i.test(url)) return url;
        return `${config.api.url}${url}`;
    };

    // Slugify helper to match country names to slug param
    const slugify = (str) => {
        try {
            return (str || '')
              .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
              .toLowerCase()
              .replace(/&/g, ' and ')
              .replace(/[^a-z0-9]+/g, '-')
              .replace(/^-+|-+$/g, '');
        } catch { return ''; }
    };

    // Resolve countryId from either id query param or slug route param
    useEffect(() => {
        const qp = new URLSearchParams(location.search);
        const idParam = qp.get('id');
        if (idParam) {
            setCountryId(parseInt(idParam, 10));
            return;
        }
        if (slug) {
            setLoading(true);
            // Fetch list and find by slug of name
            axiosPost(
                'countries/list',
                { 'Content-Type': 'application/json' },
                {},
                (resp) => {
                    const arr = Array.isArray(resp) ? resp : (Array.isArray(resp?.data) ? resp.data : []);
                    const found = arr.find(c => slugify(c.name) === slug);
                    if (found) {
                        setCountryId(parseInt(found.id, 10));
                        setError(null);
                    } else {
                        setError('Country not found');
                        setLoading(false);
                    }
                }
            );
        }
    }, [location.search, slug]);

    // Fetch country data once we have a resolved countryId
    useEffect(() => {
        const fetchCountryData = async () => {
            if (!countryId) return; // wait until resolved
            try {
                console.log('Fetching country data for ID:', countryId);
                const response = await axios.post(`${config.api.url}countries/get`, 
                    { id: Number(countryId) },
                    { 
                        headers: {
                            'Content-Type': 'application/json',
                            'api': config.api.key
                        }
                    }
                );
                
                // Store the complete response in countryInfo state
                setCountryInfo(response.data.data[0]);
                console.log('API Response:', response.data);
                
                if (response.data.success && response.data.data && response.data.data.length > 0) {
                    // The API returns data in an array, get the first item
                    const countryData = response.data.data[0];
                    console.log('Country data:', countryData);
                    
                    // Update country state with fetched data
                    setCountry(prevState => {
                        const updatedState = {
                            ...prevState,
                            name: countryData.name,
                            image: toAbsUrl(countryData.image, prevState.image),
                            intro: {
                                title: `${countryData.name} Travel Guide`,
                                body: countryData.info || prevState.intro.body
                            }
                        };
                        return updatedState;
                    });

                    // Fetch related articles for this country (limit 6)
                    try {
                        const artRes = await axios.post(
                            `${config.api.url}articles/by-country`,
                            { country_id: Number(countryId), status: 'published', per_page: 6 },
                            {
                                headers: {
                                    'Content-Type': 'application/json',
                                    'api': config.api.key
                                }
                            }
                        );
                        const items = artRes?.data?.data?.items || [];
                        const mapped = items.map((it) => ({
                            id: it.id,
                            title: it.title,
                            excerpt: it.excerpt,
                            image: toAbsUrl(it.featured_image_url),
                            slug: it.slug
                        }));
                        setCountryArticles(mapped);
                    } catch (e) {
                        console.warn('Failed to load articles by country', e);
                    }

                    // Fetch hotels for this country (active only) using axiosPost helper
                    axiosPost(
                        'hotels/by-country',
                        { 'Content-Type': 'application/json' },
                        { country_id: Number(countryId), per_page: 12 },
                        (data) => {
                            const hitems = data?.data?.items || [];
                            const mappedHotels = hitems.map((h) => ({
                                id: h.id,
                                title: h.title,
                                location: h.location || '',
                                description: h.description || '',
                                button: h.button || 'Book Now',
                                image: toAbsUrl(h.image),
                                address: h.address || '',
                            }));
                            setHootels(mappedHotels);
                        }
                    );

                    // Fetch restaurants for this country (active only) using axiosPost helper
                    axiosPost(
                        'restaurants/by-country',
                        { 'Content-Type': 'application/json' },
                        { country_id: Number(countryId), per_page: 12 },
                        (data) => {
                            const ritems = data?.data?.items || [];
                            const mappedRestaurants = ritems.map((r) => {
                                const parseImages = (val) => {
                                    if (!val) return [];
                                    if (Array.isArray(val)) return val.map(v => (v ?? '').toString().trim()).filter(Boolean);
                                    const str = typeof val === 'string' ? val : String(val);
                                    return str
                                        .split(/\r?\n|,/)
                                        .map(s => s.trim())
                                        .filter(Boolean);
                                };
                                const imgs = parseImages(r.images || '');
                                const cover = imgs.length ? imgs[0] : (r.image || '');
                                return {
                                    id: r.id,
                                    title: r.title,
                                    location: r.location || '',
                                    description: r.description || '',
                                    button: r.button || 'Reserve',
                                    image: toAbsUrl(cover)
                                };
                            });
                            setRestaurants(mappedRestaurants);
                        }
                    );

                    // Fetch experiences for this country (active only) using axiosPost helper
                    axiosPost(
                        'experiences/by-country',
                        { 'Content-Type': 'application/json' },
                        { country_id: Number(countryId), per_page: 12 },
                        (data) => {
                            const eitems = data?.data?.items || [];
                            const mappedExperiences = eitems.map((e) => {
                                const parseImages = (val) => {
                                    if (!val) return [];
                                    if (Array.isArray(val)) return val.map(v => (v ?? '').toString().trim()).filter(Boolean);
                                    const str = typeof val === 'string' ? val : String(val);
                                    return str
                                        .split(/\r?\n|,/)
                                        .map(s => s.trim())
                                        .filter(Boolean);
                                };
                                const imgs = parseImages(e.images || '');
                                const cover = imgs.length ? imgs[0] : (e.image || '');
                                return {
                                    id: e.id,
                                    title: e.title,
                                    location: e.location || '',
                                    description: e.description || '',
                                    button: e.button || 'Learn More',
                                    image: toAbsUrl(cover)
                                };
                            });
                            setExperiencesCarousel(mappedExperiences);
                        }
                    );
                } else {
                    console.error('API request successful but no country data found');
                    throw new Error(response.data.response || 'Failed to fetch country data');
                }
            } catch (err) {
                console.error('Error fetching country data:', err);
                setError(err.response?.data?.message || err.message);
            } finally {
                setLoading(false);
            }
        };
        
        fetchCountryData();
    }, [countryId]);

    // Redirect legacy /country?id=... to /country/:slug for canonical URL once name is known
    useEffect(() => {
        if (!countryInfo) return;
        const qp = new URLSearchParams(location.search);
        const hasId = qp.get('id');
        if (hasId && !slug) {
            const s = slugify(countryInfo.name || '');
            if (s) navigate(`/country/${s}`, { replace: true });
        }
    }, [countryInfo, location.search, slug, navigate]);
    
    const [ hotelIndex, setHotelIndex ] = useState(0);
    const [ hotels, setHootels ] = useState([])

    // Experiences carousel (things to do)
    const [ experienceIndex, setExperienceIndex ] = useState(0);
    const [ experiencesCarousel, setExperiencesCarousel ] = useState([
        // {
        //     title: "Snorkel at Playa Lagun",
        //     location: "Curaçao, Lagun",
        //     description: "Swim with sea turtles and explore vibrant coral reefs in one of Curaçao’s most picturesque coves.",
        //     button: "Learn More",
        //     image: "https://www.janthielresort.com/wp-content/uploads/sites/31/2020/03/Curacao-Snorkling-With-Turtles.jpg"
        // },
        // {
        //     title: "Willemstad Street Art Tour",
        //     location: "Curaçao, Willemstad",
        //     description: "Discover colorful murals and historic alleys across Otrobanda and Punda with local artist stories.",
        //     button: "Learn More",
        //     image: "https://www.besabine.com/wp-content/uploads/2023/07/Straatkunst-Willemstad.jpg"
        // },
        // {
        //     title: "Klein Curaçao Day Trip",
        //     location: "Curaçao, Klein Curaçao",
        //     description: "Sail to the uninhabited island for pristine beaches, crystal waters, and a lighthouse photo op.",
        //     button: "Book Trip",
        //     image: "https://klein-curacao.nl/wp-content/uploads/2024/05/miss-ann-boat-trips.jpg"
        // },
        // {
        //     title: "Christoffel Park Hike",
        //     location: "Curaçao, Westpunt",
        //     description: "Hike to the island’s highest peak for sweeping views and native flora and fauna.",
        //     button: "Plan Hike",
        //     image: "https://images.squarespace-cdn.com/content/v1/53b6ec86e4b09a1fb529c6b8/1568171979138-P7QL73DCJMOFE3ZB5B1J/image-asset.jpeg"
        // },
        // {
        //     title: "Shete Boka National Park",
        //     location: "Curaçao, West Coast",
        //     description: "Witness dramatic blowholes and crashing waves along rugged volcanic coastline.",
        //     button: "Explore",
        //     image: "https://www.besabine.com/wp-content/uploads/2023/07/Shete-Boka-Nationaal-Park-Curacao.jpg"
        // },
        // {
        //     title: "Floating Market & Handelskade",
        //     location: "Curaçao, Punda",
        //     description: "Stroll along the iconic waterfront and sample fresh produce from Venezuelan boats.",
        //     button: "Visit",
        //     image: "https://dynamic-media-cdn.tripadvisor.com/media/photo-o/12/31/80/41/floating-market.jpg?w=1200&h=-1&s=1"
        // }
    ])

    const [ restaurantIndex, setRestaurantIndex ] = useState(0);
    const [ restaurants, setRestaurants ] = useState([])

    const [ activeItem, setActiveItem ] = useState(0);
    const [ experiences, setExperiences ] = useState([
        {
            id: 1,
            title: "CURAÇAO MUSEUM",
            body: "Located in Otrobanda's western district, this colonial-style museum showcases Curaçao's rich heritage through geological exhibits, pre-Columbian Indian artifacts, and Caribbean historical collections. Features authentic 18th and 19th century mahogany furniture, historical maps, and artworks from local and international artists. Special focus on afro-Curaçaoan culture post-slavery abolition."
            },
        {
            id: 2,
            title: "CURAÇAO ART",
            body: "A vibrant cultural hub showcasing contemporary and traditional Curaçaoan artistic expressions. Houses rotating exhibitions featuring prominent Caribbean artists, sculpture gardens, and interactive art installations that reflect the island's diverse cultural heritage."
        },
        {
            id: 3,
            title: "FORTCHURCH LIVING MONUMENT",
            body: "Historic fortified church structure serving as both a religious site and cultural landmark. Represents a unique blend of Dutch colonial architecture and Caribbean influences. Offers guided tours highlighting its role in Curaçao's religious and military history."
        },
        {
            id: 4,
            title: "LAMAN SPA AND WELLNESS",
            body: "Premium wellness destination offering traditional Caribbean healing treatments and modern spa therapies. Features ocean-view treatment rooms, natural local ingredients, and specialized relaxation programs inspired by island traditions."
        },
        {
            id: 5,
            title: "DISTRICT OTROBANDA",
            body: "Historic neighborhood known for its colorful Dutch colonial architecture and winding streets. Home to numerous cultural attractions, local markets, and authentic restaurants. Represents one of Curaçao's most well-preserved historic districts with stunning harbor views."
        }
    ])

    const handleNext = () => {
        if (hotelIndex < hotels.length - 4) {
            setHotelIndex(hotelIndex + 2);
        }
    };

    const handlePrev = () => {
        if (hotelIndex > 0) {
            setHotelIndex(hotelIndex - 2);
        }
    };

    const handleNextRes = () => {
        if (restaurantIndex < restaurants.length - 4) {
            setRestaurantIndex(restaurantIndex + 2);
        }
    };

    const handlePrevRes = () => {
        if (restaurantIndex > 0) {
            setRestaurantIndex(restaurantIndex - 1);
        }
    };

    const handleNextExp = () => {
        if (experienceIndex < experiencesCarousel.length - 4) {
            setExperienceIndex(experienceIndex + 2);
        }
    };

    const handlePrevExp = () => {
        if (experienceIndex > 0) {
            setExperienceIndex(experienceIndex - 2);
        }
    };

    const renderBlogs = () => {

        const render_blogs = country.blogs.map((blog, index) => {
            return (
                <div className='country-blog' key={index}>
                    <div className='blog-image' style={{backgroundImage: `url(${blog.image})`, backgroundSize: 'cover'}}></div>
                    <div className='blog-info'>
                        <div className='blog-info-title'>{blog.name}</div>
                        <div className='blog-info-body'>{blog.desc}</div>
                        <div className='blog-info-author'>WRITTEN BY <span>{blog.author}</span></div>
                    </div>
                </div>
            )

        })

        return render_blogs;

    }

    const renderCountryArticles = () => {
        if (!countryArticles || countryArticles.length === 0) return null;
        const items = countryArticles.map((a, index) => {
            const articleLink = a.slug ? `/article/${a.slug}` : `/blog?id=${a.id}`;
            return (
            <Link className='article-list-item' to={articleLink} key={a.id || index}>
                <div className='article-item-image' style={{backgroundImage: `url(${a.image})`, backgroundSize: 'cover'}}></div>
                <div className='article-item-info'>
                    <div>
                        <div className='article-item-title'>{a.title}</div>
                        <div className='article-item-description'>{a.excerpt}</div>
                    </div>
                </div>
            </Link>
            );
        });
        return (
            <div className='articles'>
                <div className='articles-list-container'>
                    {items}
                </div>
            </div>
        );
    }

    const renderHotels = () => {

        const render_hotels = hotels.map((hotel, index) => {
            return (
                <Link className='hotel-item' to={`/hotel/${slugify(hotel.title)}`} key={hotel.id || index}>
                    <div className='hotel-image' style={{backgroundImage: `url(${hotel.image})`, backgroundSize: 'cover'}}></div>
                    <div className='hotel-title'>{hotel.title}</div>
                    <div className='hotel-location'>
                        <FaMapPin size={12} color={'#2c2c2c'} /> 
                        <div className='hotel-address'>{hotel.address}</div>
                    </div>
                    <div className='hotel-descr'>{hotel.description}</div>
                    <div className='hotel-btn'>{hotel.button.toUpperCase()}</div>
                </Link>
            )
        })

        return (
            <div className='hotels-track' style={{transform: `translateX(-${hotelIndex * 26}%)`}}>
                {render_hotels}
            </div>
        )

    }

    const renderRestaurants = () => {

        const render_restaurants = restaurants.map((restaurant, index) => {
            return (
                <Link className='hotel-item' to={`/restaurant/${slugify(restaurant.title)}`} key={restaurant.id || index}>
                    <div className='hotel-image' style={{backgroundImage: `url(${restaurant.image})`, backgroundSize: 'cover'}}></div>
                    <div className='hotel-title'>{restaurant.title}</div>
                    <div className='hotel-location'>
                        <FaMapPin size={12} color={'#2c2c2c'} /> 
                        <div className='hotel-address'>{(restaurant.location || '').toUpperCase()}</div>
                    </div>
                    <div className='hotel-descr'>{restaurant.description}</div>
                    <div className='hotel-btn'>{(restaurant.button || 'Reserve').toUpperCase()}</div>
                </Link>
            )
        })

        return (
            <div className='hotels-track' style={{transform: `translateX(-${restaurantIndex * 26}%)`}}>
                {render_restaurants}
            </div>
        )

    }

    const renderExperiencesCarousel = () => {
        const items = experiencesCarousel.map((exp, index) => {
            return (
                <Link className='hotel-item' to={`/experience/${slugify(exp.title)}`} key={index}>
                    <div className='hotel-image' style={{backgroundImage: `url(${exp.image})`, backgroundSize: 'cover'}}></div>
                    <div className='hotel-title'>{exp.title}</div>
                    <div className='hotel-location'>
                        <FaMapPin size={12} color={'#2c2c2c'} /> 
                        <div className='hotel-address'>{exp.location.toUpperCase()}</div>
                    </div>
                    <div className='hotel-descr'>{exp.description}</div>
                    <div className='hotel-btn'>{exp.button.toUpperCase()}</div>
                </Link>
            )
        })

        return (
            <div className='hotels-track' style={{transform: `translateX(-${experienceIndex * 26}%)`}}>
                {items}
            </div>
        )
    }

    const toggleItem = (id) => {
        setActiveItem(id === activeItem ? null : id);
    };

    const renderExperiences = () => {

        const render_experiences = experiences.map((exp, index) => {
            return (
                <div
                key={exp.id}
                className={`attraction-item ${exp.id === activeItem ? 'active' : ''}`}
                onClick={() => toggleItem(exp.id)}
                >
                    <div className='attraction-header'>{exp.title}</div>
                    <div className="attraction-body">{exp.body}</div>
                </div>
            )
        })

        return (
            <div className='curaçao-attractions-menu'>
                {render_experiences}
            </div>
        );

    }

  // Show loading state while fetching data
  if (loading) {
    return (
      <div className='country-loading'>
        <div className='loading-message'>Loading country information...</div>
      </div>
    );
  }
  
  // Show error message if API request failed
  if (error) {
    return (
      <div className='country-error'>
        <div className='error-message'>Error: {error}</div>
        <Link to='/' className='error-link'>Return to Home</Link>
      </div>
    );
  }
  
  return (
    <div className=''>
        <div className='landing-section' style={{backgroundImage: `url(${country.image})`, backgroundSize: 'cover'}}>
        </div>
        <div className='country-intro'>
            <h1 className='country-intro-title' onClick={() => {console.log(
                countryInfo
            )}}>{countryInfo.name} Travel Guide</h1>
            <div className='country-intro-body'>{country.intro.body}</div>
            <FaChevronDown size={24} color={'#2c2c2c'} style={{marginTop: '20px'}} />
        </div>

        <div className='country-blogs-headers'>
            <div className='country-section-header'>Plan your {countryInfo.name} Trip</div>
            <div className='country-section-header'>Queer Landscape</div>
        </div>
        <div className='country-blogs'>
            {renderBlogs()}
        </div>

        <div className='country-section-header'>Articles</div>
        <div className='container'>
            {renderCountryArticles()}
        </div>

        <div className='country-section-header'>Places to Stay</div>
        <div style={{width: '100%', position: 'relative', display: 'flex', alignItems: 'center'}}>
            <button className="carousel-arrow left" onClick={handlePrev}>‹</button>

            <div className='country-hotels'>
                {renderHotels()}
            </div>
            
            <button className="carousel-arrow right" onClick={handleNext}>›</button>
        </div>
       
        
        <div className='country-section-header'>Where to dine</div>
        <div style={{width: '100%', position: 'relative', display: 'flex', alignItems: 'center'}}>
            <button className="carousel-arrow left" onClick={handlePrevRes}>‹</button>

            <div className='country-hotels'>
                {renderRestaurants()}
            </div>
            
            <button className="carousel-arrow right" onClick={handleNextRes}>›</button>
        </div>

        <div className='country-section-header'>Experiences</div>
        <div style={{width: '100%', position: 'relative', display: 'flex', alignItems: 'center'}}>
            <button className="carousel-arrow left" onClick={handlePrevExp}>‹</button>

            <div className='country-hotels'>
                {renderExperiencesCarousel()}
            </div>
            
            <button className="carousel-arrow right" onClick={handleNextExp}>›</button>
        </div>
    </div>
  )
}
